import { useEffect, useRef } from 'react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
const INTERVAL_MS = 2000;

/**
 * While the customer is logged in AND the app is in the foreground, report their
 * current GPS position to the backend every ~2s. It deliberately does NOTHING in
 * the background: geolocation.watchPosition is torn down whenever the page is
 * hidden (app backgrounded/closed or screen off) and re-armed when it returns to
 * the foreground, so location is only ever sent while the app is actually open.
 *
 * Mounted inside ProtectedRoute, so it starts on login and stops on logout.
 */
export function useLocationHeartbeat(): void {
  const lastPos = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!('geolocation' in navigator)) return;

    let watchId: number | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;

    const send = () => {
      const token = localStorage.getItem('accessToken');
      const pos = lastPos.current;
      if (!token || !pos) return;
      // keepalive lets the final beat go out even as the tab is being hidden.
      fetch(`${API_URL}/rides/my-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(pos),
        keepalive: true,
      }).catch(() => {});
    };

    const start = () => {
      if (watchId !== null || document.hidden) return;
      watchId = navigator.geolocation.watchPosition(
        (p) => { lastPos.current = { lat: p.coords.latitude, lng: p.coords.longitude }; },
        () => {},   // permission denied / unavailable: just send nothing
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 },
      );
      timer = setInterval(send, INTERVAL_MS);
    };

    const stop = () => {
      if (watchId !== null) { navigator.geolocation.clearWatch(watchId); watchId = null; }
      if (timer !== null) { clearInterval(timer); timer = null; }
    };

    const onVisibility = () => { if (document.hidden) stop(); else start(); };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);
}
